import { LoginPromptPage } from "components/App/components/LoginWall/components/LoginPromptPage";
import { useGlobalErrorContext } from "context/globalErrorContext";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";
import { useOpenGlobalErrorModal } from "hooks/useGlobalError";
import { useEffect } from "react";
import { Route, Routes } from "react-router";
import { setCsrfToken } from "utils/csrf";
import ApiError from "utils/errors/apiError";
import { type TErrorId } from "utils/errors/mapApiErrorParams";

export const LoginWall: FC = ({ children }) => {
  const { isLoggedIn, loginDetails, reloadUser } = useAuthenticatedUser();
  const {
    actions: { setIsLoginPage },
  } = useGlobalErrorContext();
  const openGlobalErrorModal = useOpenGlobalErrorModal();

  useEffect(() => {
    const listener = (e: Event) => {
      const customEvent = e as CustomEvent;
      const searchParams = customEvent.detail as URLSearchParams;
      const provider = searchParams.get("provider") as "google" | "microsoft" | "okta" | undefined;
      const method = searchParams.get("method") as "oidc" | "saml" | undefined;
      const csrfToken = searchParams.get("csrfToken") as string | undefined;
      if (csrfToken) {
        setCsrfToken(csrfToken);
      }
      void reloadUser(provider && method ? { method, provider } : undefined);
    };
    addEventListener("loginSuccess", listener);
    return () => removeEventListener("loginSuccess", listener);
  }, [loginDetails, reloadUser]);

  useEffect(() => {
    const listener = (e: Event) => {
      const customEvent = e as CustomEvent;
      const searchParams = customEvent.detail as URLSearchParams;
      const errorIdBase64 = searchParams.get("errorId");
      let errorId: TErrorId = "request.internalServerError";
      if (errorIdBase64) errorId = Buffer.from(errorIdBase64, "base64").toString("utf-8") as TErrorId;
      openGlobalErrorModal(new ApiError({ errorId }));
    };
    addEventListener("loginFailure", listener);
    return () => removeEventListener("loginFailure", listener);
  }, [openGlobalErrorModal]);

  useEffect(() => {
    setIsLoginPage(!isLoggedIn);
  }, [setIsLoginPage, isLoggedIn]);

  if (!isLoggedIn) {
    return (
      <Routes>
        <Route path="/">
          <Route element={<LoginPromptPage />} index />
          <Route element={<LoginPromptPage />} path="*" />
        </Route>
      </Routes>
    );
  }

  return <>{children}</>;
};
